import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { compressLogo } from "@/lib/imageCompress";
import { getDLToken } from "@/lib/dlToken";
import { fmtDate } from "@/lib/datetime";
import { formatPhone, formatPhoneInput } from "@/lib/phone";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pill, Pencil, UserPlus, PackagePlus, Phone, MapPin, X, Search, Image as ImageIcon, Upload, RefreshCw } from "lucide-react";
import PatientOrdersDialog from "@/components/PatientOrdersDialog";
import AutopayPanel from "@/components/AutopayPanel";
import { MonthlyOrdersChart } from "@/components/MonthlyOrdersChart";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// ============================ Pharmacy form
const EMPTY_PRICING = {
  same_day: { "0-9": 0, "10-19": 0, "20-29": 0, "30-39": 0, "40-49": 0 },
  next_day: { "0-9": 0, "10-19": 0, "20-29": 0, "30-39": 0, "40-49": 0 },
  otc:      { "0-9": 0, "10-19": 0, "20-29": 0, "30-39": 0, "40-49": 0 },
  pickup:   { "0-9": 0, "10-19": 0, "20-29": 0, "30-39": 0, "40-49": 0 },
};
const BANDS = ["0-9", "10-19", "20-29", "30-39", "40-49"];
const PRICE_TYPES = [
  { v: "same_day", label: "Same Day" },
  { v: "next_day", label: "Next Day" },
  { v: "otc", label: "OTC" },
  { v: "pickup", label: "Pick Up" },
];
const EMPTY_LABEL_CONFIG = { hide_order_number: false, hide_priority: false, hide_items: false };
const EMPTY_PHARMACY = { name: "", nickname: "", address: "", phone: "", billing_email: "", alert_emails: ["", "", ""], pricing: EMPTY_PRICING, order_numbering: "auto", region: "south_fl", label_config: { ...EMPTY_LABEL_CONFIG } };

function PharmacyDialog({ open, onOpenChange, initial, onSubmit, title, submitLabel, pharmacyId }) {
  const [form, setForm] = useState(initial || EMPTY_PHARMACY);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(initial?.logo_url || null);
  const [activeRoutes, setActiveRoutes] = useState(0);
  useEffect(() => {
    const base = initial || EMPTY_PHARMACY;
    const pricing = { ...EMPTY_PRICING };
    if (base.pricing) for (const t of PRICE_TYPES) pricing[t.v] = { ...EMPTY_PRICING[t.v], ...(base.pricing[t.v] || {}) };
    const label_config = { ...EMPTY_LABEL_CONFIG, ...(base.label_config || {}) };
    setForm({ ...EMPTY_PHARMACY, ...base, pricing, label_config });
    setLogoUrl(base.logo_url || null);
  }, [initial, open]);

  // Fetch active-route count once when the edit dialog opens
  useEffect(() => {
    if (!open || !pharmacyId) { setActiveRoutes(0); return; }
    api.get(`/pharmacies/${pharmacyId}/active-routes-count`)
      .then(({ data }) => setActiveRoutes(data?.count || 0))
      .catch(() => setActiveRoutes(0));
  }, [open, pharmacyId]);

  const addressChanged = !!pharmacyId && (form.address || "").trim() !== (initial?.address || "").trim();
  const showRouteWarning = addressChanged && activeRoutes > 0;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const logoSrc = logoUrl ? `${BACKEND_URL}${logoUrl}?auth=${encodeURIComponent(getDLToken())}` : null;

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !pharmacyId) return;
    setLogoBusy(true);
    try {
      // Logos arrive as 3–5 MB screenshots; we resize to 600px (longest side)
      // before upload. Keeps PNG alpha if the source was PNG.
      const compressed = await compressLogo(f);
      const fd = new FormData();
      fd.append("logo", compressed);
      const { data } = await api.post(`/pharmacies/${pharmacyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.logo_url);
      toast.success("Logo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLogoBusy(false); e.target.value = ""; }
  };

  const setPrice = (type, band, value) => {
    setForm((f) => ({ ...f, pricing: { ...f.pricing, [type]: { ...f.pricing[type], [band]: value === "" ? 0 : Number(value) } } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="pharmacy-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pharmacy-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="pharmacy-phone" /></div>
          </div>
          <div>
            <Label>Billing email <span className="text-muted-foreground text-xs font-normal">(invoices are emailed here)</span></Label>
            <Input
              type="email"
              value={form.billing_email || ""}
              onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
              placeholder="billing@pharmacy.com"
              data-testid="pharmacy-billing-email"
            />
          </div>
          {/* Up to 3 emails that receive Restricted-ZIP + Failed-Delivery alerts.
              Blank slots are ignored server-side. */}
          <div>
            <Label>
              Alert emails
              <span className="text-muted-foreground text-xs font-normal"> (up to 3 · receive Restricted ZIP + Failed Delivery notifications)</span>
            </Label>
            <div className="grid md:grid-cols-3 gap-2 mt-1" data-testid="pharmacy-alert-emails-row">
              {[0, 1, 2].map((idx) => (
                <Input
                  key={idx}
                  type="email"
                  value={form.alert_emails?.[idx] || ""}
                  onChange={(e) => {
                    const next = Array.isArray(form.alert_emails) ? [...form.alert_emails] : ["", "", ""];
                    while (next.length < 3) next.push("");
                    next[idx] = e.target.value;
                    setForm({ ...form, alert_emails: next });
                  }}
                  placeholder={idx === 0 ? "alert1@pharmacy.com" : idx === 1 ? "alert2@pharmacy.com" : "alert3@pharmacy.com"}
                  data-testid={`pharmacy-alert-email-${idx + 1}`}
                />
              ))}
            </div>
          </div>
          <div>
            <Label>Nickname <span className="text-muted-foreground text-xs font-normal">(short label shown in the Orders list)</span></Label>
            <Input
              value={form.nickname || ""}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
              placeholder="e.g. Hialeah, Pembroke, Miami Lake"
              maxLength={32}
              data-testid="pharmacy-nickname"
            />
          </div>
          <div><Label>Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="pharmacy-address" /></div>
          {showRouteWarning && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-2"
              data-testid="pharmacy-route-warning"
            >
              <span className="text-base leading-none">⚠️</span>
              <span>
                Saving this change will <span className="font-semibold">recompute {activeRoutes} active route{activeRoutes === 1 ? "" : "s"}</span> that start from this pharmacy (the start point, polyline and ETA will refresh).
              </span>
            </div>
          )}
          <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="pharmacy-region-section">
            <div className="flex items-baseline justify-between mb-2">
              <Label className="text-sm font-medium">Workspace / region</Label>
              <span className="text-xs text-muted-foreground">Determines who sees this pharmacy</span>
            </div>
            <Select value={form.region || "south_fl"} onValueChange={(v) => setForm({ ...form, region: v })}>
              <SelectTrigger data-testid="pharmacy-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="south_fl">South FL — Broward + Miami-Dade</SelectItem>
                <SelectItem value="central_fl">Central FL — Orlando + Kissimmee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="pharmacy-order-numbering-section">
            <div className="flex items-baseline justify-between mb-2">
              <Label className="text-sm font-medium">Order # numbering</Label>
              <span className="text-xs text-muted-foreground">Choose who assigns the Order #</span>
            </div>
            <Select value={form.order_numbering || "auto"} onValueChange={(v) => setForm({ ...form, order_numbering: v })}>
              <SelectTrigger data-testid="pharmacy-order-numbering"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatic — system generates a sequential Order # (e.g. 000001)</SelectItem>
                <SelectItem value="manual">Manual — pharmacy enters their own Order # on each order</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {pharmacyId && (
            <div className="rounded-xl border border-dashed border-border p-3 flex items-center gap-3" data-testid="pharmacy-logo-section">
              <div className="h-14 w-14 rounded-lg bg-secondary/40 border border-border flex items-center justify-center overflow-hidden shrink-0">
                {logoSrc ? <img src={logoSrc} alt="logo" className="object-contain h-full w-full" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">Pharmacy logo</div>
                <div className="text-xs text-muted-foreground">PNG/JPG up to 5MB. Used on 4×6 shipping labels.</div>
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} disabled={logoBusy} data-testid="pharmacy-logo-input" />
                <span className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                  <Upload className="h-3.5 w-3.5" /> {logoBusy ? "Uploading…" : (logoUrl ? "Replace" : "Upload")}
                </span>
              </label>
            </div>
          )}
          <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="pharmacy-label-config-section">
            <div className="flex items-baseline justify-between mb-2">
              <Label className="text-sm font-medium">Shipping label — hide fields</Label>
              <span className="text-xs text-muted-foreground">Turn on to remove that section from THIS pharmacy&apos;s 4×6 labels</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              {[
                { key: "hide_order_number", label: "Order #", testid: "label-hide-order-number" },
                { key: "hide_priority", label: "Priority", testid: "label-hide-priority" },
                { key: "hide_items", label: "Items list", testid: "label-hide-items" },
              ].map((opt) => (
                <label key={opt.key} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm cursor-pointer hover:bg-accent/10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={!!form.label_config?.[opt.key]}
                    onChange={(e) => setForm({ ...form, label_config: { ...(form.label_config || {}), [opt.key]: e.target.checked } })}
                    data-testid={opt.testid}
                  />
                  <span>Hide <strong>{opt.label}</strong></span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <Label className="text-base font-display">Pricing ($)</Label>
              <span className="text-xs text-muted-foreground">By deliver type & distance from pharmacy</span>
            </div>
            <div className="rounded-xl border border-border overflow-hidden" data-testid="pricing-grid">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Deliver type</th>
                    {BANDS.map((b) => <th key={b} className="px-3 py-2 text-center font-medium">{b} mi</th>)}
                  </tr>
                </thead>
                <tbody>
                  {PRICE_TYPES.map((t) => (
                    <tr key={t.v} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{t.label}</td>
                      {BANDS.map((b) => (
                        <td key={b} className="px-1 py-1.5">
                          <Input type="number" step="0.01" min="0" value={form.pricing?.[t.v]?.[b] ?? 0}
                            onChange={(e) => setPrice(t.v, b, e.target.value)} className="text-center font-mono-num h-9"
                            data-testid={`price-${t.v}-${b}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="pharmacy-submit">{busy ? "Saving…" : submitLabel}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ Patient form + create-order
function PatientFormDialog({ open, onOpenChange, initial, pharmacyId, onSubmit, title, submitLabel }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial || { name: "", phone: "", address: "", notes: "" }); }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit({ ...form, pharmacy_id: pharmacyId }); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="patient-form">
          <div><Label>Patient name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="patient-name" /></div>
          <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="patient-phone" /></div>
          <div><Label>Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="patient-address" /></div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="patient-submit">{busy ? "Saving…" : submitLabel}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ITEM_OPTIONS = ["Regular Medication", "Controlled Medication", "Refrigerated"];
const DELIVER_TYPES = [
  { v: "same_day", label: "Same Day" },
  { v: "next_day", label: "Next Day" },
  { v: "otc", label: "OTC" },
  { v: "pickup", label: "Pick Up" },
];

function CreateOrderFromPatient({ patient, pharmacyName, pharmacy, open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const canSetCopay = user?.role === "super_admin" || user?.role === "pharmacy_staff";
  const [items, setItems] = useState([{ name: "Regular Medication", qty: 1, notes: "" }]);
  const [deliverType, setDeliverType] = useState("same_day");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [copayEnabled, setCopayEnabled] = useState(false);
  const [copay, setCopay] = useState("");
  const [busy, setBusy] = useState(false);
  const manualNumbering = (pharmacy?.order_numbering || "auto") === "manual";

  useEffect(() => {
    if (open) {
      setItems([{ name: "Regular Medication", qty: 1, notes: "" }]);
      setDeliverType("same_day"); setPriority("normal");
      // Pull patient.notes into the order's Delivery notes so dispatchers
      // don't re-type recurring instructions.
      setNotes(patient?.notes || "");
      setOrderNumber("");
      setCopayEnabled(false); setCopay("");
    }
  }, [open, patient?.notes]);

  const setItem = (i, k, v) => setItems((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { name: "Regular Medication", qty: 1, notes: "" }]);
  const removeItem = (i) => setItems((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
    if (!cleaned.length) { toast.error("Add at least one item"); return; }
    if (manualNumbering && !orderNumber.trim()) {
      toast.error("This pharmacy uses manual order numbering — please enter an Order #.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        customer_name: patient.name, customer_phone: patient.phone, address: patient.address,
        pharmacy_id: patient.pharmacy_id, items: cleaned, priority, deliver_type: deliverType, notes: notes || null,
      };
      if (manualNumbering) payload.order_number = orderNumber.trim();
      if (canSetCopay && copayEnabled && copay !== "") {
        const n = Number(copay);
        if (Number.isFinite(n) && n >= 0) payload.copay = n;
      }
      await api.post("/orders", payload);
      toast.success(`Order created for ${patient.name}`);
      onCreated?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="create-order-from-patient">
        <DialogHeader><DialogTitle>New order for {patient?.name}</DialogTitle></DialogHeader>
        <div className="space-y-1 mb-2 p-3 rounded-lg bg-secondary/40 text-sm">
          <div className="font-medium">{patient?.name}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3 w-3" />{formatPhone(patient?.phone)}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{patient?.address}</div>
          <div className="text-xs text-muted-foreground mt-1">From {pharmacyName}</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Deliver type</Label>
              <Select value={deliverType} onValueChange={setDeliverType}>
                <SelectTrigger data-testid="new-order-deliver-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DELIVER_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="cold_chain">Cold Chain</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {manualNumbering ? (
            <div data-testid="patient-order-number-section">
              <Label>Order # <span className="text-destructive">*</span></Label>
              <Input
                required
                placeholder="e.g. RX-2026-00045"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                data-testid="patient-order-number-input"
              />
              <p className="text-xs text-muted-foreground mt-1">{pharmacyName} uses manual order numbering.</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="patient-order-number-auto-hint">
              Order # will be generated automatically by the system.
            </p>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-7">
                    <Select value={it.name} onValueChange={(v) => setItem(i, "name", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ITEM_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2" type="number" min={1} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input className="col-span-2" placeholder="notes" value={it.notes} onChange={(e) => setItem(i, "notes", e.target.value)} />
                  <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Delivery notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Apartment buzzer, allergies…" />
          </div>
          {canSetCopay && (
            <div data-testid="quick-copay-section">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={copayEnabled}
                  onCheckedChange={(v) => { setCopayEnabled(!!v); if (!v) setCopay(""); }}
                  data-testid="quick-copay-toggle"
                />
                <span className="text-sm">Charge a copay</span>
              </Label>
              {copayEnabled && (
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={copay}
                    onChange={(e) => setCopay(e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="quick-copay"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="new-order-submit">{busy ? "Creating…" : "Create order"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ Main page
export default function AdminPharmacies() {
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);

  const selected = useMemo(() => pharmacies.find((p) => p.id === selectedId) || null, [pharmacies, selectedId]);

  const loadPharmacies = () => api.get("/pharmacies", { params: { include_inactive: true } }).then((r) => {
    setPharmacies(r.data);
    if (!selectedId && r.data[0]) setSelectedId(r.data[0].id);
  });
  const loadPatients = () => {
    if (!selectedId) return;
    api.get("/patients", { params: { pharmacy_id: selectedId } }).then((r) => setPatients(r.data));
  };
  useEffect(() => { loadPharmacies(); }, []);
  useEffect(() => { loadPatients(); setPatientSearch(""); }, [selectedId]);

  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) =>
      [p.name, p.phone, p.address, p.notes].some((v) => v && v.toLowerCase().includes(q))
    );
  }, [patients, patientSearch]);

  const createPharmacy = async (form) => { await api.post("/pharmacies", form); toast.success("Pharmacy added"); loadPharmacies(); };
  const updatePharmacy = async (form) => { await api.put(`/pharmacies/${editTarget.id}`, form); toast.success("Pharmacy updated"); setEditTarget(null); loadPharmacies(); };
  const togglePharmacyActive = async (p, next) => {
    try {
      await api.patch(`/pharmacies/${p.id}/active`, { active: next });
      toast.success(next ? `${p.name} activated` : `${p.name} deactivated`);
      loadPharmacies();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deletePharmacy = async (id) => {
    if (!confirm("Delete this pharmacy?")) return;
    await api.delete(`/pharmacies/${id}`); toast.success("Pharmacy deleted");
    if (selectedId === id) setSelectedId(null);
    loadPharmacies();
  };

  // One-click logo upload from the left-side pharmacy list — drops the user
  // straight into a file picker without opening the Edit dialog.
  const quickUploadLogo = async (pharmacyId, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !pharmacyId) return;
    const compressed = await compressLogo(f);
    const fd = new FormData();
    fd.append("logo", compressed);
    try {
      await api.post(`/pharmacies/${pharmacyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded — it will appear on labels");
      loadPharmacies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Re-geocode a single pharmacy's address — typically used when the pin
  // is in the wrong place on the map.
  const [regeocodingId, setRegeocodingId] = useState(null);
  const regeocodePharmacy = async (ph) => {
    if (!ph) return;
    if (!confirm(`Re-geocode pharmacy address?\n\n"${ph.address}"\n\nThis re-resolves the pin from the address text. Any active routes starting from this pharmacy will refresh.`)) return;
    setRegeocodingId(ph.id);
    try {
      const { data } = await api.post(`/pharmacies/${ph.id}/regeocode`);
      toast.success(`Pin moved to ${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`);
      loadPharmacies();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegeocodingId(null);
    }
  };

  const createPatient = async (form) => { await api.post("/patients", form); toast.success("Patient added"); loadPatients(); };
  const updatePatient = async (form) => { await api.put(`/patients/${editPatient.id}`, form); toast.success("Patient updated"); setEditPatient(null); loadPatients(); };
  const deletePatient = async (p) => {
    if (!confirm(`Delete patient ${p.name}?`)) return;
    await api.delete(`/patients/${p.id}`); toast.success("Patient removed"); loadPatients();
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="admin-pharmacies-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Pharmacies</h1>
          <p className="text-muted-foreground text-sm mt-1">Select a pharmacy on the left to view its patients.</p>
        </div>
        <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-pharmacy-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add pharmacy
        </Button>
      </header>

      <div className="mb-6"><MonthlyOrdersChart /></div>

      <PharmacyDialog open={addOpen} onOpenChange={setAddOpen} initial={EMPTY_PHARMACY} onSubmit={createPharmacy} title="New pharmacy" submitLabel="Add pharmacy" />
      <PharmacyDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}
        initial={editTarget ? { name: editTarget.name, nickname: editTarget.nickname || "", address: editTarget.address, phone: editTarget.phone || "", billing_email: editTarget.billing_email || "", alert_emails: Array.isArray(editTarget.alert_emails) ? [...editTarget.alert_emails, "", "", ""].slice(0, 3) : ["", "", ""], pricing: editTarget.pricing, logo_url: editTarget.logo_url, order_numbering: editTarget.order_numbering || "auto", region: editTarget.region || "south_fl", label_config: editTarget.label_config || { ...EMPTY_LABEL_CONFIG } } : EMPTY_PHARMACY}
        pharmacyId={editTarget?.id}
        onSubmit={updatePharmacy} title="Edit pharmacy" submitLabel="Save changes" />

      <PatientFormDialog open={addPatientOpen} onOpenChange={setAddPatientOpen} initial={null} pharmacyId={selectedId} onSubmit={createPatient} title="New patient" submitLabel="Add patient" />
      <PatientFormDialog open={!!editPatient} onOpenChange={(o) => !o && setEditPatient(null)}
        initial={editPatient ? { name: editPatient.name, phone: editPatient.phone, address: editPatient.address, notes: editPatient.notes || "" } : null}
        pharmacyId={selectedId} onSubmit={updatePatient} title="Edit patient" submitLabel="Save changes" />

      <CreateOrderFromPatient patient={orderTarget} pharmacy={selected} pharmacyName={selected?.name} open={!!orderTarget} onOpenChange={(o) => !o && setOrderTarget(null)} onCreated={loadPatients} />
      <PatientOrdersDialog patient={historyTarget} open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)} />

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Left: pharmacy list */}
        <Card className="rounded-xl border-border bg-card/80 overflow-hidden h-fit sticky top-6" data-testid="pharmacy-list">
          <div className="px-4 py-3 border-b border-border bg-secondary/40 text-xs uppercase tracking-wider font-medium text-muted-foreground">
            Pharmacies ({pharmacies.length})
          </div>
          <ul className="max-h-[70vh] overflow-y-auto scrollbar-thin">
            {pharmacies.map((p) => {
              const isActive = p.active !== false;
              const logoSrc = p.logo_url
                ? `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}?auth=${encodeURIComponent(getDLToken())}`
                : null;
              return (
              <li key={p.id} className="relative">
                <button
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left flex items-start gap-2.5 px-4 py-3 border-b border-border transition-colors ${!isActive ? "opacity-60" : ""} ${
                    selectedId === p.id ? "bg-primary/10 border-l-4 border-l-primary" : "hover:bg-secondary/40 border-l-4 border-l-transparent"
                  }`}
                  data-testid={`pharmacy-nav-${p.id}`}
                >
                  {/* Logo thumbnail / upload slot — clickable independent of the row */}
                  <label
                    onClick={(e) => e.stopPropagation()}
                    className={`relative h-10 w-10 rounded-lg shrink-0 overflow-hidden cursor-pointer border ${
                      logoSrc ? "border-border bg-white" : "border-dashed border-border bg-secondary/50 hover:bg-secondary"
                    } grid place-items-center group/logo`}
                    title={logoSrc ? "Replace logo" : "Upload pharmacy logo"}
                    data-testid={`pharmacy-logo-slot-${p.id}`}
                  >
                    {logoSrc ? (
                      <>
                        <img src={logoSrc} alt={`${p.name} logo`} className="h-full w-full object-contain" />
                        <span className="absolute inset-0 grid place-items-center bg-black/55 opacity-0 group-hover/logo:opacity-100 transition-opacity">
                          <Upload className="h-4 w-4 text-white" />
                        </span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold grid place-items-center">
                          +
                        </span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => quickUploadLogo(p.id, e)}
                      data-testid={`pharmacy-logo-upload-${p.id}`}
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm truncate ${selectedId === p.id ? "text-primary" : ""}`}>
                      {p.name}
                      {p.nickname && <span className="ml-1.5 text-xs font-normal text-muted-foreground">· {p.nickname}</span>}
                      {!isActive && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`pharmacy-inactive-badge-${p.id}`}>Inactive</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{p.address}</div>
                  </div>
                </button>
              </li>
              );
            })}
            {pharmacies.length === 0 && <li className="px-4 py-8 text-center text-sm text-muted-foreground">No pharmacies yet.</li>}
          </ul>
        </Card>

        {/* Right: patients of selected pharmacy */}
        <div className="min-w-0" data-testid="patients-pane">
          {!selected && (
            <Card className="p-12 rounded-xl border-border bg-card/80 text-center text-muted-foreground">
              Select a pharmacy on the left to view its patients.
            </Card>
          )}

          {selected && (
            <>
              <Card className="p-5 rounded-xl border-border bg-card/80 mb-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display font-bold text-xl tracking-tight">
                      {selected.name}
                      {selected.nickname && <span className="ml-2 text-sm font-normal text-muted-foreground">· {selected.nickname}</span>}
                    </h2>
                    <div className="text-sm text-muted-foreground mt-0.5 flex items-start gap-2">
                      <span className="flex-1">{selected.address}</span>
                      <button
                        type="button"
                        onClick={() => regeocodePharmacy(selected)}
                        disabled={regeocodingId === selected.id}
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                        data-testid={`pharmacy-regeocode-${selected.id}`}
                        title="Re-geocode this pharmacy's address — use when the pin is off"
                      >
                        <RefreshCw className={`h-3 w-3 ${regeocodingId === selected.id ? "animate-spin" : ""}`} />
                        {regeocodingId === selected.id ? "Re-geocoding…" : "Re-geocode"}
                      </button>
                    </div>
                    {selected.phone && <div className="text-xs text-muted-foreground mt-0.5">{formatPhone(selected.phone)}</div>}
                    {selected.lat && <div className="text-xs font-mono-num text-accent mt-1">{selected.lat.toFixed(4)}, {selected.lng.toFixed(4)}</div>}
                    <span
                      className={`inline-block mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${
                        (selected.order_numbering || "auto") === "manual"
                          ? "bg-amber-500/15 text-amber-700"
                          : "bg-primary/10 text-primary"
                      }`}
                      data-testid="pharmacy-order-numbering-badge"
                    >
                      Order # · {(selected.order_numbering || "auto") === "manual" ? "Manual" : "Automatic"}
                    </span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer mr-1.5 px-2 py-1 rounded-full border border-border" title={selected.active === false ? "Click to activate" : "Click to deactivate"}>
                      <Checkbox
                        checked={selected.active !== false}
                        onCheckedChange={(v) => togglePharmacyActive(selected, !!v)}
                        data-testid={`pharmacy-active-toggle-${selected.id}`}
                      />
                      <span className="uppercase tracking-wider font-medium">{selected.active === false ? "Inactive" : "Active"}</span>
                    </label>
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => setAddPatientOpen(true)} data-testid="add-patient-btn">
                      <UserPlus className="h-4 w-4 mr-1.5" /> Add patient
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditTarget(selected)} className="text-muted-foreground hover:text-primary" data-testid={`pharmacy-edit-${selected.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deletePharmacy(selected.id)} className="text-muted-foreground hover:text-destructive" data-testid={`pharmacy-delete-${selected.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Autopay panel — admin can inspect enrollment status, view
                  last-charge outcome, or (with pharmacy consent) enroll on
                  their behalf. Every state change is audited + emailed to
                  the super admin. Keyed on selected.id so switching pharmacy
                  remounts the panel and fetches fresh state. */}
              <div className="mb-4" data-testid="admin-autopay-section">
                <AutopayPanel key={selected.id} pharmacyId={selected.id} pharmacyName={selected.name} />
              </div>

              <Card className="rounded-xl border-border bg-card/80 overflow-hidden" data-testid="patients-table">
                <div className="px-5 py-3 bg-secondary/50 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
                    Patients ({filteredPatients.length}{patientSearch ? ` / ${patients.length}` : ""})
                  </div>
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      placeholder="Search patient name, phone, address…"
                      className="pl-8 h-9 text-sm"
                      data-testid="pharmacy-patient-search"
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr className="border-b border-border">
                        <th className="px-5 py-2 font-medium">Patient</th>
                        <th className="px-5 py-2 font-medium">Phone</th>
                        <th className="px-5 py-2 font-medium">Address</th>
                        <th className="px-5 py-2 font-medium">Orders</th>
                        <th className="px-5 py-2 font-medium">Last order</th>
                        <th className="px-5 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPatients.map((p) => (
                        <tr key={p.id} className="border-b border-border hover:bg-secondary/30" data-testid={`patient-row-${p.id}`}>
                          <td className="px-5 py-3 font-medium">
                            <button
                              type="button"
                              onClick={() => setHistoryTarget(p)}
                              className="text-primary hover:underline underline-offset-2 text-left"
                              data-testid={`patient-name-link-${p.id}`}
                              title="View order history"
                            >
                              {p.name}
                            </button>
                          </td>
                          <td className="px-5 py-3 text-muted-foreground font-mono-num text-xs">{formatPhone(p.phone)}</td>
                          <td className="px-5 py-3 text-muted-foreground max-w-[280px] truncate">{p.address}</td>
                          <td className="px-5 py-3 font-mono-num text-xs">{p.total_orders || 0}</td>
                          <td className="px-5 py-3 text-xs text-muted-foreground">{p.last_order_at ? fmtDate(p.last_order_at) : "—"}</td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <Button size="icon" onClick={() => setOrderTarget(p)} className="h-9 w-9 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid={`new-order-btn-${p.id}`} title="Create new order">
                              <PackagePlus className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setEditPatient(p)} className="text-muted-foreground hover:text-primary" data-testid={`patient-edit-${p.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deletePatient(p)} className="text-muted-foreground hover:text-destructive" data-testid={`patient-delete-${p.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {filteredPatients.length === 0 && (
                        <tr><td colSpan={6} className="px-5 py-12 text-center text-muted-foreground text-sm">
                          {patients.length === 0
                            ? `No patients yet for ${selected.name}. Click "Add patient" or create an order to register one.`
                            : `No patients match "${patientSearch}".`}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
